import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Eye, EyeOff, AlertTriangle, ArrowLeft, Briefcase, Lock, Mail, ShieldCheck,
  ChevronRight, User as UserIcon, FileText, UserPlus, ShieldAlert,
  Globe, Phone, MessageCircle,
} from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// Forms.app application links — kept as constants so they're easy to swap
// without touching multiple call sites.
const SIA_APPLICATION_URL = 'https://f6qaezs4.forms.app/sansecurity';
const CSO_APPLICATION_URL = 'https://f6qaezs4.forms.app/sansecuritycso';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const isRegister = searchParams.get('register') === 'true';
  const inviteToken = searchParams.get('token');
  const inactivityReason = searchParams.get('reason') === 'inactivity';

  // One-shot toast when the user lands here after being auto-logged-out.
  // A ref guards against a double-fire in React strict mode.
  const inactivityToastShown = useRef(false);
  useEffect(() => {
    if (inactivityReason && !inactivityToastShown.current) {
      inactivityToastShown.current = true;
      toast.error('You have been logged out due to inactivity. Please log in again.', {
        duration: 8000,
      });
    }
  }, [inactivityReason]);

  const [isLogin, setIsLogin] = useState(!isRegister && !inviteToken);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isLogin) {
        const user = await login(email, password, rememberMe);
        toast.success(`Welcome back, ${user.name}!`);
        // The ProtectedRoute + VerificationProvider will redirect to
        // /verify if the user needs to verify their email. Router
        // navigation to the role-default is safe either way.
        const redirectPath = user.role === 'admin' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : user.role === 'client' ? '/client' : '/employee';
        navigate(redirectPath);
      } else {
        const user = await register(email, password, name, inviteToken);
        toast.success(`Welcome to San Security, ${user.name}!`);
        // New invite-registered users always start with an email
        // verification gate — send them straight to /verify so the
        // context has time to fetch without an extra redirect.
        navigate('/verify');
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Unable to connect. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setResetSent(true);
      toast.success('If the email exists, a new temporary password has been sent');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send reset email';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ---- Secondary flows render as branded centred cards (no split layout) ----
  if (isForgotPassword) {
    return (
      <CenteredCard title="FORGOT PASSWORD" subtitle="Enter your email and we'll send you a new temporary password">
        {resetSent ? (
          <div className="text-center space-y-4">
            <p className="text-green-500" data-testid="forgot-password-success">Temporary password sent!</p>
            <p className="text-sm text-muted-foreground">Check your email inbox (and spam folder). Log in with the temporary password, then change it from your profile.</p>
            <Button onClick={() => { setIsForgotPassword(false); setResetSent(false); }} className="mt-4 bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
              Back to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <FormField label="Email" id="forgot-email" icon={Mail}>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 bg-background border-border focus:ring-primary rounded-sm h-11"
              />
            </FormField>
            <Button
              type="submit" disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm h-11 uppercase tracking-wider font-medium"
            >
              {loading ? 'Sending...' : 'Send Temporary Password'}
            </Button>
            <button
              type="button"
              onClick={() => setIsForgotPassword(false)}
              className="w-full text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Login
            </button>
          </form>
        )}
      </CenteredCard>
    );
  }

  // Registration without an invite is blocked — show a branded notice.
  if (!isLogin && !inviteToken) {
    return (
      <CenteredCard title="INVITATION REQUIRED" subtitle="New accounts are admin-issued only">
        <div className="space-y-4" data-testid="invite-required-notice">
          <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/40 rounded-sm p-4">
            <ShieldAlert className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-foreground">Invitation required</p>
              <p className="text-muted-foreground">
                New accounts can only be created from an invitation link sent by an administrator.
                Please contact your supervisor and ask them to email you an invite.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setIsLogin(true)}
            data-testid="back-to-login-btn"
            className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm h-11 uppercase tracking-wider font-medium"
          >
            Back to Sign In
          </Button>
        </div>
      </CenteredCard>
    );
  }

  // ---- Invite-registration form (only when inviteToken is present) ----
  if (!isLogin && inviteToken) {
    return (
      <CenteredCard title="COMPLETE REGISTRATION" subtitle="Welcome to S.A.N Security">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Full Name" id="reg-name" icon={UserIcon}>
            <Input
              id="reg-name" data-testid="name-input" type="text" placeholder="John Smith"
              value={name} onChange={(e) => setName(e.target.value)} required
              className="pl-10 bg-background border-border focus:ring-primary rounded-sm h-11"
            />
          </FormField>
          <FormField label="Email" id="reg-email" icon={Mail}>
            <Input
              id="reg-email" data-testid="email-input" type="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              className="pl-10 bg-background border-border focus:ring-primary rounded-sm h-11"
            />
          </FormField>
          <FormField label="Password" id="reg-password" icon={Lock}>
            <Input
              id="reg-password" data-testid="password-input"
              type={showPassword ? 'text' : 'password'} placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)} required
              className="pl-10 pr-10 bg-background border-border focus:ring-primary rounded-sm h-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </FormField>
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-sm">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          <Button
            type="submit" data-testid="submit-btn" disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm h-11 uppercase tracking-wider font-medium"
          >
            {loading ? 'Please wait...' : 'Create Account'}
          </Button>
        </form>
      </CenteredCard>
    );
  }

  // ---- Main LOGIN view: header + two cards + notice footer ----
  return (
    <div className="min-h-screen bg-[#eef0f3] flex flex-col" data-testid="login-page">
      {/* TOP — dark brand header */}
      <header className="bg-[#14181f] text-white px-4 py-4 sm:py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-3 sm:gap-4">
          <img
            src="/san-logo.jpg"
            alt="S.A.N Security"
            className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-sm shadow-[0_0_25px_rgba(250,204,21,0.3)]"
          />
          <div className="text-center sm:text-left">
            <h1 className="font-barlow tracking-widest text-xl sm:text-3xl leading-none">
              S.A.N <span className="text-white">SECURITY</span> <span className="text-primary">LTD</span>
            </h1>
            <p className="text-[10px] sm:text-sm tracking-[0.25em] text-primary uppercase mt-1 font-semibold">
              Your Safety. Our Priority.
            </p>
          </div>
        </div>
      </header>

      {/* CONTENT — login card + contact card */}
      <div className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-6 py-2 sm:py-8 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 items-start">
        {/* LEFT — Welcome Back / login */}
        <div className="bg-white border border-zinc-200 rounded-lg shadow-sm p-4 sm:p-8 animate-fadeUp">
          <div className="text-center">
            <h2 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">Welcome Back</h2>
            <p className="text-xs sm:text-base text-zinc-500 mt-0.5 sm:mt-1">Log in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-3 sm:mt-6 space-y-2.5 sm:space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-zinc-800">Email Address</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 h-10 sm:h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-primary rounded-md"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-zinc-800">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 pr-10 h-10 sm:h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-primary rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  data-testid="remember-me-checkbox"
                  className="w-4 h-4 rounded-sm border-zinc-300 accent-[#FACC15]"
                />
                <span className="text-sm text-zinc-700">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setIsForgotPassword(true)}
                data-testid="forgot-password-btn"
                className="text-sm text-primary hover:underline font-medium"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-md">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="submit-btn"
              disabled={loading}
              className="w-full h-10 sm:h-12 bg-[#14181f] text-white hover:bg-[#232a35] rounded-md uppercase tracking-[0.2em] font-semibold transition-all active:scale-[0.99]"
            >
              {loading ? 'Please wait…' : 'Log In'}
            </Button>
          </form>

          {/* Divider */}
          <div className="my-2.5 sm:my-5 flex items-center gap-3" aria-hidden>
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs tracking-[0.3em] text-zinc-500 uppercase">or</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          {/* New to S.A.N */}
          <div className="text-center">
            <p className="text-sm sm:text-base font-semibold text-zinc-900">New to S.A.N Security Ltd?</p>
            <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">
              Please read our{' '}
              <Link to="/terms-to-join" data-testid="terms-link" className="text-primary font-medium hover:underline">
                terms and conditions
              </Link>{' '}
              before joining our team.
            </p>
          </div>

          <div className="mt-2.5 sm:mt-4 space-y-2">
            <button
              type="button"
              onClick={() => navigate('/terms-to-join')}
              data-testid="mobile-menu-terms"
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-md border border-zinc-200 bg-white hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all text-left"
            >
              <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-700" strokeWidth={1.75} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-zinc-900">Terms &amp; Conditions</span>
                <span className="block text-[11px] sm:text-xs text-zinc-500">Read our terms and conditions to join</span>
              </span>
              <ChevronRight className="w-4 h-4 text-zinc-400 ml-auto" />
            </button>
            <button
              type="button"
              onClick={() => setApplyOpen(true)}
              data-testid="apply-cta-btn"
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-md border border-zinc-200 bg-white hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all text-left"
            >
              <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-700" strokeWidth={1.75} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-zinc-900">Recruitment Form</span>
                <span className="block text-[11px] sm:text-xs text-zinc-500">Not a member? Apply for a job</span>
              </span>
              <ChevronRight className="w-4 h-4 text-zinc-400 ml-auto" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/faq')}
              data-testid="public-faq-btn"
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-md border border-zinc-200 bg-white hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all text-left"
            >
              <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
                <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-700" strokeWidth={1.75} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-zinc-900">FAQ</span>
                <span className="block text-[11px] sm:text-xs text-zinc-500">Frequently asked questions</span>
              </span>
              <ChevronRight className="w-4 h-4 text-zinc-400 ml-auto" />
            </button>
            {/* Contact Details — menu row on mobile (card hidden below lg) */}
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="contact-menu-row"
              className="lg:hidden w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-md border border-zinc-200 bg-white hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all text-left"
            >
              <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-700" strokeWidth={1.75} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-zinc-900">Contact Details</span>
                <span className="block text-[11px] sm:text-xs text-zinc-500">Website, WhatsApp, phone &amp; email</span>
              </span>
              <ChevronRight className="w-4 h-4 text-zinc-400 ml-auto" />
            </button>
          </div>
        </div>

        {/* RIGHT — Contact Details (desktop only; on mobile use the menu row above) */}
        <div className="hidden lg:block bg-white border border-zinc-200 rounded-lg shadow-sm p-4 sm:p-8 animate-fadeUp" data-testid="contact-block" style={{ animationDelay: '80ms' }}>
          <div className="text-center">
            <h2 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">Contact Details</h2>
            <p className="text-xs sm:text-base text-zinc-500 mt-0.5 sm:mt-1">Get in touch with us</p>
          </div>

          <div className="mt-3 sm:mt-6 grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-0 sm:divide-y sm:divide-zinc-100">
            <button
              type="button"
              onClick={() => navigate('/contact')}
              className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4 p-2.5 sm:px-2 sm:py-4 rounded-md sm:rounded-none border sm:border-0 border-zinc-100 hover:bg-zinc-50 active:bg-zinc-100 transition-colors w-full"
              data-testid="contact-website"
            >
              <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.75} />
              </span>
              <span className="text-center sm:text-left">
                <span className="block text-xs sm:text-base font-semibold text-zinc-900">Website</span>
                <span className="block text-[11px] sm:text-sm text-primary font-medium">www.sansecurity.uk</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4 p-2.5 sm:px-2 sm:py-4 rounded-md sm:rounded-none border sm:border-0 border-zinc-100 hover:bg-zinc-50 active:bg-zinc-100 transition-colors w-full"
              data-testid="contact-whatsapp"
            >
              <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" strokeWidth={1.75} />
              </span>
              <span className="text-center sm:text-left">
                <span className="block text-xs sm:text-base font-semibold text-zinc-900">WhatsApp</span>
                <span className="block text-[11px] sm:text-sm text-primary font-medium">07365 928174</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4 p-2.5 sm:px-2 sm:py-4 rounded-md sm:rounded-none border sm:border-0 border-zinc-100 hover:bg-zinc-50 active:bg-zinc-100 transition-colors w-full"
              data-testid="contact-phone"
            >
              <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.75} />
              </span>
              <span className="text-center sm:text-left">
                <span className="block text-xs sm:text-base font-semibold text-zinc-900">Phone</span>
                <span className="block text-[11px] sm:text-sm text-primary font-medium">0800 707 4059</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4 p-2.5 sm:px-2 sm:py-4 rounded-md sm:rounded-none border sm:border-0 border-zinc-100 hover:bg-zinc-50 active:bg-zinc-100 transition-colors w-full"
              data-testid="contact-email"
            >
              <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.75} />
              </span>
              <span className="text-center sm:text-left">
                <span className="block text-xs sm:text-base font-semibold text-zinc-900">Email</span>
                <span className="block text-[10px] sm:text-sm text-primary font-medium break-all">sansecuryty@gmail.com</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* FOOTER — Important Notice */}
      <footer className="bg-[#14181f] text-white px-4 py-2.5 sm:py-8 text-center">
        <div className="max-w-2xl mx-auto">
          <ShieldAlert className="w-5 h-5 sm:w-8 sm:h-8 text-primary mx-auto" strokeWidth={1.5} />
          <p className="mt-1 sm:mt-2 text-xs sm:text-lg font-bold">Important Notice</p>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-sm text-white/70 leading-relaxed">
            S.A.N Security Ltd reserves the right to accept or decline any application and to modify
            or update these terms and conditions at any time without prior notice.
          </p>
          <p className="mt-1 sm:mt-3 text-[9px] sm:text-xs text-white/50 flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3" /> All information is kept secure and confidential.
          </p>
        </div>
      </footer>


      {/* Apply-for-job picker */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-widest uppercase text-primary text-lg">
              Apply for a job
            </DialogTitle>
            <DialogDescription>
              Choose the role you&apos;d like to apply for. The application form opens in a new tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <ApplyChoice
              icon={ShieldCheck}
              title="SIA Door Supervisor"
              description="Front-line venue security with a valid SIA badge."
              testId="sia-application-btn"
              onClick={() => {
                window.open(SIA_APPLICATION_URL, '_blank', 'noopener,noreferrer');
                setApplyOpen(false);
              }}
            />
            <ApplyChoice
              icon={UserIcon}
              title="CSO Steward"
              description="Customer Service Officer / Steward — events & crowd safety."
              testId="cso-application-btn"
              onClick={() => {
                window.open(CSO_APPLICATION_URL, '_blank', 'noopener,noreferrer');
                setApplyOpen(false);
              }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Forms are hosted on our secure Forms.app workspace.
          </p>
        </DialogContent>
      </Dialog>

      {/* Local CSS for the fade-up entrance */}
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
        .animate-fadeUp { animation: fadeUp .55s ease-out both; }
      `}</style>
    </div>
  );
}

// ---------- Sub-components ----------

function ApplyChoice({ icon: Icon, title, description, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left flex items-start gap-3 px-4 py-3 rounded-sm border border-border bg-background hover:border-primary hover:bg-primary/5 transition-colors"
    >
      <div className="p-2 rounded-sm bg-primary/10 text-primary flex-shrink-0">
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-barlow tracking-widest uppercase text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground mt-2.5 flex-shrink-0" />
    </button>
  );
}

function CenteredCard({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-card">
      <div className="absolute inset-0 grid-pattern opacity-25" aria-hidden />
      <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur-sm border-border rounded-sm">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 rounded-sm overflow-hidden shadow-[0_0_30px_rgba(250,204,21,0.25)]">
            <img src="/san-logo.jpg" alt="S.A.N Security" className="w-full h-full object-cover" />
          </div>
          <div>
            <CardTitle className="text-2xl font-barlow tracking-widest text-primary">{title}</CardTitle>
            <CardDescription className="text-muted-foreground mt-2">{subtitle}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

function FormField({ label, id, icon: Icon, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
        {children}
      </div>
    </div>
  );
}
